import { useEffect, useRef, useState } from 'react';
import { BellRing } from 'lucide-react';
import { Button, Description, Modal, useOverlayState } from '@heroui/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getNotificationPreferences,
  notificationsKeys,
  setPushEnabledPreference,
} from '../../../api/notifications';
import { AsyncButton } from '../../../components/ui/async-button';
import { useAuth } from '../../../hooks/useAuth';
import { usePushNotifications } from '../../../hooks/usePushNotifications';
import { showError, showSuccess, showWarning } from '../../../lib/toast';
import { detectInstallPlatform, readPwaStandalone } from '../../auth/utils/pwa-install';
import { getPushActivateErrorMessage } from '../utils/push-activate-error';
import { interpretPushActivation } from '../utils/push-activation';
import { dismissPushOptIn, wasPushOptInDismissed } from '../utils/push-opt-in';

function isIosSafariNotInstalled(): boolean {
  if (typeof navigator === 'undefined') return false;
  return detectInstallPlatform(navigator.userAgent, navigator.maxTouchPoints) === 'ios' &&
    !readPwaStandalone();
}

/**
 * Depois do login, pede adesão às notificações com um gesto do usuário
 * (o Chrome/Safari só abrem o prompt nativo a partir de um clique).
 */
export function PushOptInDialog() {
  const { authUser } = useAuth();
  const queryClient = useQueryClient();
  const { isSupported, permission, register } = usePushNotifications();
  const state = useOverlayState();
  const openDialog = state.open;
  const [activating, setActivating] = useState(false);
  const activatingRef = useRef(false);
  const acceptedRef = useRef(false);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (!authUser || !isSupported || permission !== 'default') return;
    if (wasPushOptInDismissed(authUser.uid)) return;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        const prefs = await getNotificationPreferences().catch(() => null);
        if (cancelled) return;
        if (prefs?.configured && !prefs.pushEnabled) return;
        openDialog();
      })();
    }, 700);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [authUser, isSupported, permission, openDialog]);

  useEffect(() => {
    if (state.isOpen) {
      wasOpenRef.current = true;
      return;
    }
    if (wasOpenRef.current && authUser && !acceptedRef.current) {
      dismissPushOptIn(authUser.uid);
    }
    wasOpenRef.current = false;
  }, [authUser, state.isOpen]);

  const handleLater = () => {
    if (authUser) dismissPushOptIn(authUser.uid);
    state.close();
  };

  const handleActivate = async () => {
    if (activatingRef.current) return;
    activatingRef.current = true;
    setActivating(true);
    try {
      const outcome = interpretPushActivation(await register());
      if (outcome === 'denied') {
        showWarning(
          'Permita nas configurações do site ou do iPhone.',
          'Notificações bloqueadas',
        );
        if (authUser) dismissPushOptIn(authUser.uid);
        state.close();
        return;
      }
      if (outcome === 'failed') {
        showError(getPushActivateErrorMessage(), 'Não foi possível ativar');
        return;
      }

      await setPushEnabledPreference(true).catch(() => undefined);
      await queryClient.invalidateQueries({ queryKey: notificationsKeys.preferences() });
      acceptedRef.current = true;
      if (authUser) dismissPushOptIn(authUser.uid);
      showSuccess('Você receberá avisos neste aparelho.', 'Notificações ativadas');
      state.close();
    } catch {
      const outcome = interpretPushActivation(false);
      if (outcome === 'denied') {
        showWarning(
          'Permita nas configurações do site ou do iPhone.',
          'Notificações bloqueadas',
        );
        if (authUser) dismissPushOptIn(authUser.uid);
        state.close();
        return;
      }
      if (outcome === 'activated') {
        acceptedRef.current = true;
        if (authUser) dismissPushOptIn(authUser.uid);
        showSuccess('Você receberá avisos neste aparelho.', 'Notificações ativadas');
        state.close();
        return;
      }
      showError(getPushActivateErrorMessage(), 'Não foi possível ativar');
    } finally {
      activatingRef.current = false;
      setActivating(false);
    }
  };

  return (
    <Modal state={state}>
      <Modal.Backdrop isDismissable={!activating} className="z-[200]">
        <Modal.Container placement="center" className="z-[200]">
          <Modal.Dialog className="w-full max-w-md">
            <Modal.Header className="border-b border-separator pb-3">
              <div className="flex flex-col gap-1 pr-8">
                <Modal.Heading>Receber notificações?</Modal.Heading>
                <Description>
                  Avisamos no celular quando pedirem sua vaga, aprovarem um empréstimo ou
                  reportarem mau uso.
                </Description>
              </div>
              <Modal.CloseTrigger />
            </Modal.Header>

            <Modal.Body className="flex flex-col gap-4 py-4">
              <div className="flex items-start gap-3 rounded-2xl bg-surface-secondary px-3 py-3">
                <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent">
                  <BellRing size={22} />
                </span>
                <p className="text-sm leading-relaxed text-muted">
                  Você escolhe o que recebe depois, em Perfil. Sem spam — só o que importa no
                  condomínio.
                </p>
              </div>

              {isIosSafariNotInstalled() ? (
                <p className="text-sm text-muted">
                  No iPhone, o aviso só funciona com o Link Park na tela inicial (Safari →
                  Compartilhar → Adicionar à Tela de Início).
                </p>
              ) : null}
            </Modal.Body>

            <Modal.Footer className="flex justify-end gap-2 border-t border-separator pt-3">
              <Button variant="ghost" isDisabled={activating} onPress={handleLater}>
                Agora não
              </Button>
              <AsyncButton
                variant="primary"
                isPending={activating}
                pendingLabel="Ativar"
                onPress={() => void handleActivate()}
              >
                <BellRing size={16} />
                Ativar
              </AsyncButton>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
